import { useEffect, useMemo, useState } from 'react';
import { api } from '../api/client';
import { confirmDialog } from '../utils/confirm';

const DAYS = [
  { n: 1, l: 'Lundi' }, { n: 2, l: 'Mardi' }, { n: 3, l: 'Mercredi' },
  { n: 4, l: 'Jeudi' }, { n: 5, l: 'Vendredi' }, { n: 6, l: 'Samedi' },
];

const CHANGE_TYPES = { REMPLACEMENT: 'Remplacement', ANNULATION: 'Annulation', SALLE: 'Changement de salle', DEPLACEMENT: 'Déplacement' };

const SUBJECT_COLORS = [
  '#4e79c4', '#e06c45', '#7cb87a', '#c97dbf', '#e8b84b',
  '#5cb8b8', '#d4727a', '#8b7ccc', '#7abdd4', '#c4a96e',
  '#6eb87a', '#cc8a4e', '#9c7ab8', '#4eaac4', '#c4c44e',
];

function getSubjectColor(subjectId, colorMap) {
  if (colorMap[subjectId]) return colorMap[subjectId];
  const keys = Object.keys(colorMap);
  colorMap[subjectId] = SUBJECT_COLORS[keys.length % SUBJECT_COLORS.length];
  return colorMap[subjectId];
}

// Génère les créneaux horaires de dayStart à dayEnd par pas de slotMinutes
function generateSlots(dayStart = '07:00', dayEnd = '18:00', slotMinutes = 30) {
  const slots = [];
  const [sh, sm] = dayStart.split(':').map(Number);
  const [eh, em] = dayEnd.split(':').map(Number);
  let cur = sh * 60 + sm;
  const end = eh * 60 + em;
  while (cur <= end) {
    const h = String(Math.floor(cur / 60)).padStart(2, '0');
    const m = String(cur % 60).padStart(2, '0');
    slots.push(`${h}:${m}`);
    cur += slotMinutes;
  }
  return slots;
}

function timeToMin(t) {
  const [h, m] = (t || '00:00').split(':').map(Number);
  return h * 60 + m;
}

function TimetableGrid() {
  const [ref, setRef] = useState({ classes: [], subjects: [], teachers: [], rooms: [] });
  const [settings, setSettings] = useState({ dayStart: '07:00', dayEnd: '18:00', slotMinutes: 30 });
  const [view, setView] = useState('class');
  const [selId, setSelId] = useState('');
  const [sessions, setSessions] = useState([]);
  const [changes, setChanges] = useState([]);
  const [error, setError] = useState('');
  const [form, setForm] = useState({ dayOfWeek: 1, startTime: '08:00', endTime: '09:00', subjectId: '', teacherId: '', roomId: '' });
  const [changeFor, setChangeFor] = useState(null);
  const [mode, setMode] = useState('normal'); // normal | compact
  const [editMode, setEditMode] = useState(false);
  const [addModal, setAddModal] = useState(null); // { day, time }
  const colorMap = useMemo(() => ({}), []);

  useEffect(() => {
    Promise.all([
      api.get('/catalog/classes'), api.get('/catalog/subjects'),
      api.get('/catalog/teachers'), api.get('/catalog/rooms'),
      api.get('/timetable/settings'),
      api.get('/staff?type=ENSEIGNANT&limit=500').catch(() => []),
    ]).then(([classes, subjects, teachers, rooms, s, staffRaw]) => {
      const staffList = Array.isArray(staffRaw) ? staffRaw : staffRaw?.data || [];
      console.log('STAFF count:', staffList.length, 'GANEM:', staffList.find(x => x.lastName?.toLowerCase().includes('ganem')));
      console.log('TEACHERS count:', teachers.length, 'GANEM in teachers:', teachers.find(x => x.lastName?.toLowerCase().includes('ganem')));
      const suspendedNames = new Set(
        staffList.filter((st) => st.status !== 'ACTIF').map((st) => `${(st.lastName || '').trim().toLowerCase()} ${(st.firstName || '').trim().toLowerCase()}`)
      );
      console.log('suspendedNames:', [...suspendedNames]);
      const activeTeachers = teachers.filter((t) => {
        const key = `${(t.lastName || '').trim().toLowerCase()} ${(t.firstName || '').trim().toLowerCase()}`;
        return !suspendedNames.has(key);
      });
      setRef({ classes, subjects, teachers: activeTeachers, rooms });
      if (classes[0]) setSelId(classes[0].id);
      setForm((f) => ({ ...f, subjectId: subjects[0]?.id || '', teacherId: teachers[0]?.id || '' }));
      if (s) setSettings({ dayStart: s.dayStart || '07:00', dayEnd: s.dayEnd || '18:00', slotMinutes: s.slotMinutes || 30 });
    }).catch((e) => setError(e.message));
    loadChanges();
  }, []);

  useEffect(() => {
    const list = view === 'class' ? ref.classes : view === 'teacher' ? ref.teachers : ref.rooms;
    if (list && list[0]) setSelId(list[0].id);
  }, [view]);

  function load() {
    if (!selId) return;
    const param = view === 'class' ? 'classId' : view === 'teacher' ? 'teacherId' : 'roomId';
    api.get(`/timetable?${param}=${selId}`).then(setSessions).catch((e) => setError(e.message));
  }
  useEffect(load, [view, selId]);
  function loadChanges() { api.get('/timetable/changes').then(setChanges).catch(() => {}); }

  const slots = useMemo(() => generateSlots(settings.dayStart, settings.dayEnd, settings.slotMinutes), [settings]);

  // Organiser les sessions par jour
  const byDay = useMemo(() => {
    const m = {};
    DAYS.forEach((d) => { m[d.n] = []; });
    sessions.forEach((s) => { (m[s.dayOfWeek] = m[s.dayOfWeek] || []).push(s); });
    return m;
  }, [sessions]);

  // Trouver la session qui couvre un créneau donné
  function sessionForSlot(dayN, slotTime) {
    return (byDay[dayN] || []).find((s) => {
      const start = timeToMin(s.startTime);
      const end = timeToMin(s.endTime);
      const slot = timeToMin(slotTime);
      return slot >= start && slot < end;
    });
  }

  // Vérifier si c'est le début d'une session
  function isSessionStart(dayN, slotTime) {
    return (byDay[dayN] || []).find((s) => s.startTime === slotTime);
  }

  // Calculer la hauteur en nombre de slots
  function sessionSlotCount(s) {
    const dur = timeToMin(s.endTime) - timeToMin(s.startTime);
    return Math.max(1, Math.round(dur / (settings.slotMinutes || 30)));
  }

  function cardMeta(s) {
    if (view === 'class') return `${s.teacher?.lastName || '—'}${s.room?.name ? ` · ${s.room.name}` : ''}`;
    if (view === 'teacher') return `${s.class?.name || '—'}${s.room?.name ? ` · ${s.room.name}` : ''}`;
    return `${s.class?.name || ''} · ${s.teacher?.lastName || ''}`;
  }

  async function removeSession(id) {
    if (!(await confirmDialog({ title: 'Supprimer', message: 'Supprimer ce créneau ?', confirmLabel: 'Supprimer', danger: true }))) return;
    try { await api.del(`/timetable/${id}`); load(); } catch (err) { setError(err.message); }
  }
  async function removeChange(id) { try { await api.del(`/timetable/changes/${id}`); loadChanges(); } catch (err) { setError(err.message); } }

  async function addSession(f) {
    try {
      await api.post('/timetable', {
        classId: selId, subjectId: f.subjectId, teacherId: f.teacherId, roomId: f.roomId || undefined,
        dayOfWeek: Number(f.dayOfWeek), startTime: f.startTime, endTime: f.endTime,
      });
      load();
    } catch (err) { setError(err.message); }
  }

  // KPIs
  const totalCours = sessions.length;
  const matieres = new Set(sessions.map((s) => s.subjectId)).size;
  const enseignants = new Set(sessions.map((s) => s.teacherId)).size;
  const totalHeures = sessions.reduce((sum, s) => sum + (timeToMin(s.endTime) - timeToMin(s.startTime)), 0) / 60;

  // Infos classe sélectionnée
  const selectedClass = view === 'class' ? ref.classes.find((c) => c.id === selId) : null;
  const selectedTeacher = view === 'teacher' ? ref.teachers.find((t) => t.id === selId) : null;

  // Répartition matières
  const subjectStats = useMemo(() => {
    const map = {};
    sessions.forEach((s) => {
      const name = s.subject?.name || '—';
      const dur = (timeToMin(s.endTime) - timeToMin(s.startTime)) / 60;
      if (!map[name]) map[name] = { name, count: 0, hours: 0, subjectId: s.subjectId };
      map[name].count++;
      map[name].hours += dur;
    });
    return Object.values(map).sort((a, b) => b.hours - a.hours);
  }, [sessions]);

  const dimList = view === 'class' ? ref.classes : view === 'teacher' ? ref.teachers : ref.rooms;
  const dimLabel = (x) => view === 'teacher' ? `${x.lastName} ${x.firstName}` : x.name;
  const slotH = mode === 'compact' ? 28 : 44;

  return (
    <>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12, flexWrap: 'wrap', gap: 10 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: '1.4rem', fontWeight: 700 }}>
            Emploi du temps — {selId ? (dimList.find((x) => x.id === selId) ? dimLabel(dimList.find((x) => x.id === selId)) : '—') : '—'}
          </h1>
          <p style={{ margin: 0, color: 'var(--muted)', fontSize: '0.85rem' }}>Année scolaire 2025-2026</p>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button className="btn-ghost btn-sm" onClick={() => window.print()}>↓ Exporter</button>
          <button className="btn-ghost btn-sm" onClick={() => setAddModal({ day: 1, time: '08:00' })}>Modèles</button>
          <button className="btn btn-sm" onClick={() => setAddModal({ day: 1, time: '08:00' })}>+ Ajouter un cours</button>
        </div>
      </div>

      {/* KPIs */}
      <div className="kpi-grid" style={{ marginBottom: 16 }}>
        <div className="kpi"><span>Total cours</span><strong>{totalCours}</strong></div>
        <div className="kpi"><span>Matières</span><strong>{matieres}</strong></div>
        <div className="kpi"><span>Enseignants</span><strong>{enseignants}</strong></div>
        <div className="kpi gold"><span>Heures/sem</span><strong>{totalHeures.toFixed(1)}h</strong></div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 280px', gap: 16, alignItems: 'start' }}>
        {/* Grille principale */}
        <div className="panel">
          <div className="panel-head" style={{ flexWrap: 'wrap', gap: 8 }}>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <div className="tabs" style={{ margin: 0 }}>
                {[['class', 'Par classe'], ['teacher', 'Par enseignant'], ['room', 'Par salle']].map(([v, l]) => (
                  <button key={v} className={`tab ${view === v ? 'active' : ''}`} onClick={() => setView(v)}>{l}</button>
                ))}
              </div>
              <select style={{ width: 170 }} value={selId} onChange={(e) => setSelId(e.target.value)}>
                {dimList.map((x) => <option key={x.id} value={x.id}>{dimLabel(x)}</option>)}
              </select>
            </div>
            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <button className={`tab ${mode === 'normal' ? 'active' : ''}`} onClick={() => setMode('normal')}>Normal</button>
              <button className={`tab ${mode === 'compact' ? 'active' : ''}`} onClick={() => setMode('compact')}>Compact</button>
              <button
                className={`tab ${editMode ? 'active' : ''}`}
                onClick={() => setEditMode(!editMode)}
                style={{ color: editMode ? 'var(--danger)' : '' }}
              >✏ Mode édition</button>
            </div>
          </div>
          <div className="panel-body" style={{ padding: 0, overflowX: 'auto' }}>
            {error && <div className="form-error" style={{ margin: 8 }}>{error}</div>}
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: mode === 'compact' ? '0.75rem' : '0.82rem' }}>
              <thead>
                <tr>
                  <th style={{ width: 58, padding: '8px 4px', textAlign: 'center', color: 'var(--muted)', fontWeight: 500, borderBottom: '2px solid var(--line)', position: 'sticky', top: 0, background: 'var(--surface)' }}>Heure</th>
                  {DAYS.map((d) => (
                    <th key={d.n} style={{ padding: '8px 4px', textAlign: 'center', fontWeight: 600, borderBottom: '2px solid var(--line)', borderLeft: '1px solid var(--line)', position: 'sticky', top: 0, background: 'var(--surface)' }}>{d.l}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {slots.map((slotTime, si) => {
                  // Vérifier quels jours ont une session qui COMMENCE ici
                  const rowSessions = DAYS.map((d) => isSessionStart(d.n, slotTime));
                  // Vérifier quels jours ont une session en cours (pas le début)
                  const rowContinue = DAYS.map((d) => {
                    const ongoing = sessionForSlot(d.n, slotTime);
                    const starting = isSessionStart(d.n, slotTime);
                    return ongoing && !starting ? ongoing : null;
                  });

                  return (
                    <tr key={slotTime} style={{ height: slotH }}>
                      <td style={{ textAlign: 'right', padding: '0 6px 0 0', color: 'var(--muted)', fontSize: '0.72rem', verticalAlign: 'top', paddingTop: 2, borderBottom: '1px solid var(--line-soft, #f0f0f0)', whiteSpace: 'nowrap' }}>
                        {slotTime}
                      </td>
                      {DAYS.map((d, di) => {
                        const sess = rowSessions[di];
                        const cont = rowContinue[di];

                        if (cont) return null; // déjà couvert par rowspan

                        if (sess) {
                          const rows = sessionSlotCount(sess);
                          const color = getSubjectColor(sess.subjectId, colorMap);
                          return (
                            <td key={d.n} rowSpan={rows}
                              style={{ borderLeft: '1px solid var(--line)', borderBottom: '1px solid var(--line-soft, #f0f0f0)', padding: 2, verticalAlign: 'top' }}>
                              <div style={{
                                background: color + '22', borderLeft: `3px solid ${color}`,
                                borderRadius: 4, padding: mode === 'compact' ? '2px 4px' : '4px 6px',
                                height: '100%', boxSizing: 'border-box', position: 'relative', cursor: 'pointer',
                                minHeight: slotH * rows - 4,
                              }}>
                                <div style={{ color, fontWeight: 700, fontSize: mode === 'compact' ? '0.72rem' : '0.78rem', lineHeight: 1.2 }}>{sess.subject?.name}</div>
                                <div style={{ color: 'var(--muted)', fontSize: '0.68rem', lineHeight: 1.2 }}>{sess.startTime}–{sess.endTime}</div>
                                {mode === 'normal' && <div style={{ color: 'var(--text)', fontSize: '0.7rem' }}>{cardMeta(sess)}</div>}
                                {editMode && (
                                  <div style={{ position: 'absolute', top: 2, right: 2, display: 'flex', gap: 2 }}>
                                    <button style={{ fontSize: '0.65rem', padding: '1px 4px', cursor: 'pointer', background: 'white', border: '1px solid var(--line)', borderRadius: 3 }}
                                      onClick={() => setChangeFor(sess)}>⇄</button>
                                    {view === 'class' && (
                                      <button style={{ fontSize: '0.65rem', padding: '1px 4px', cursor: 'pointer', background: 'white', border: '1px solid var(--danger)', borderRadius: 3, color: 'var(--danger)' }}
                                        onClick={() => removeSession(sess.id)}>×</button>
                                    )}
                                  </div>
                                )}
                              </div>
                            </td>
                          );
                        }

                        return (
                          <td key={d.n} style={{ borderLeft: '1px solid var(--line)', borderBottom: '1px solid var(--line-soft, #f0f0f0)', padding: 2, verticalAlign: 'top' }}>
                            {editMode && view === 'class' && (
                              <button
                                onClick={() => setAddModal({ day: d.n, time: slotTime })}
                                style={{ width: '100%', height: '100%', minHeight: slotH - 4, border: 'none', background: 'transparent', cursor: 'pointer', color: 'var(--muted)', fontSize: '1rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                +
                              </button>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* Panneau latéral */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {/* Infos classe/enseignant */}
          <div className="panel">
            <div className="panel-body" style={{ padding: 14 }}>
              {selectedClass && (
                <>
                  <div style={{ fontWeight: 600, marginBottom: 6, color: 'var(--muted)', fontSize: '0.8rem' }}>INFORMATIONS DE LA CLASSE</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    <div><span style={{ color: 'var(--muted)', fontSize: '0.78rem' }}>Classe</span><br /><b>{selectedClass.name}</b></div>
                    <div><span style={{ color: 'var(--muted)', fontSize: '0.78rem' }}>Niveau</span><br /><b>{selectedClass.level?.name || '—'}</b></div>
                    <div><span style={{ color: 'var(--muted)', fontSize: '0.78rem' }}>Année académique</span><br /><b>2025-2026</b></div>
                    {selectedClass.studentCount != null && (
                      <div><span style={{ color: 'var(--muted)', fontSize: '0.78rem' }}>Nombre d'élèves</span><br /><b>{selectedClass.studentCount}</b></div>
                    )}
                  </div>
                </>
              )}
              {selectedTeacher && (
                <>
                  <div style={{ fontWeight: 600, marginBottom: 6, color: 'var(--muted)', fontSize: '0.8rem' }}>INFORMATIONS ENSEIGNANT</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    <div><b>{selectedTeacher.lastName} {selectedTeacher.firstName}</b></div>
                    <div><span style={{ color: 'var(--muted)', fontSize: '0.78rem' }}>Matière principale</span><br /><b>{selectedTeacher.subject || selectedTeacher.mainSubject || '—'}</b></div>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Répartition matières */}
          <div className="panel">
            <div className="panel-body" style={{ padding: 14 }}>
              <div style={{ fontWeight: 600, marginBottom: 10, color: 'var(--muted)', fontSize: '0.8rem' }}>RÉPARTITION DES MATIÈRES</div>
              {subjectStats.length === 0 ? <div style={{ color: 'var(--muted)', fontSize: '0.82rem' }}>Aucun cours.</div> : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {subjectStats.map((s) => {
                    const color = getSubjectColor(s.subjectId, colorMap);
                    return (
                      <div key={s.name} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flex: 1, minWidth: 0 }}>
                          <div style={{ width: 10, height: 10, borderRadius: 2, background: color, flexShrink: 0 }} />
                          <span style={{ fontSize: '0.82rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.name}</span>
                        </div>
                        <div style={{ textAlign: 'right', flexShrink: 0 }}>
                          <div style={{ fontSize: '0.78rem', color: 'var(--muted)' }}>{s.count} cours/sem</div>
                          <div style={{ fontSize: '0.82rem', fontWeight: 600 }}>{s.hours.toFixed(1)}h</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* Changements à venir */}
          {changes.length > 0 && (
            <div className="panel">
              <div className="panel-body" style={{ padding: 14 }}>
                <div style={{ fontWeight: 600, marginBottom: 8, color: 'var(--muted)', fontSize: '0.8rem' }}>CHANGEMENTS À VENIR</div>
                {changes.slice(0, 3).map((c) => (
                  <div key={c.id} style={{ fontSize: '0.78rem', marginBottom: 6, paddingBottom: 6, borderBottom: '1px solid var(--line)' }}>
                    <span className={`badge ${c.type === 'ANNULATION' ? 'red' : 'navy'}`} style={{ fontSize: '0.65rem' }}>{CHANGE_TYPES[c.type]}</span>
                    <div style={{ marginTop: 2 }}>{c.session?.subject?.name} · {new Date(c.date).toLocaleDateString('fr-FR')}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Remplacements */}
      {changes.length > 0 && (
        <div className="panel" style={{ marginTop: 16 }}>
          <div className="panel-head"><h2>Remplacements & changements</h2></div>
          <div className="panel-body">
            <div className="table-wrap"><table>
              <thead><tr><th>Date</th><th>Type</th><th>Séance</th><th>Détail</th><th>Motif</th><th></th></tr></thead>
              <tbody>{changes.map((c) => (
                <tr key={c.id}>
                  <td className="mono">{new Date(c.date).toLocaleDateString('fr-FR')}</td>
                  <td><span className={`badge ${c.type === 'ANNULATION' ? 'red' : 'navy'}`}>{CHANGE_TYPES[c.type] || c.type}</span></td>
                  <td>{c.session?.class?.name} · {c.session?.subject?.name} <small className="mono" style={{ color: 'var(--muted)' }}>{c.session?.startTime}-{c.session?.endTime}</small></td>
                  <td>{c.substituteTeacher ? `Remp. : ${c.substituteTeacher}` : ''}{c.newRoom ? ` Salle : ${c.newRoom}` : ''}{!c.substituteTeacher && !c.newRoom ? '—' : ''}</td>
                  <td>{c.reason || '—'}</td>
                  <td style={{ textAlign: 'right' }}><button className="link-danger" onClick={() => removeChange(c.id)}>Retirer</button></td>
                </tr>
              ))}</tbody>
            </table></div>
          </div>
        </div>
      )}

      {changeFor && <ChangeModal session={changeFor} onClose={() => setChangeFor(null)} onSaved={() => { setChangeFor(null); loadChanges(); }} />}
      {addModal && (
        <AddSessionModal
          initial={{ ...form, dayOfWeek: addModal.day, startTime: addModal.time }}
          subjects={ref.subjects}
          teachers={ref.teachers}
          rooms={ref.rooms}
          onClose={() => setAddModal(null)}
          onSaved={(f) => { addSession(f); setAddModal(null); }}
        />
      )}
    </>
  );
}

function AddSessionModal({ initial, subjects, teachers, rooms, onClose, onSaved }) {
  const [f, setF] = useState({ ...initial });
  const [error, setError] = useState('');
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));

  function submit(e) {
    e.preventDefault();
    if (!f.subjectId || !f.teacherId) { setError('Matière et enseignant sont obligatoires.'); return; }
    onSaved(f);
  }

  return (
    <div className="modal-overlay">
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <h2>Ajouter un cours</h2>
        {error && <div className="form-error">{error}</div>}
        <div className="grid-3">
          <div className="field"><label>Jour</label>
            <select value={f.dayOfWeek} onChange={set('dayOfWeek')}>
              {DAYS.map((d) => <option key={d.n} value={d.n}>{d.l}</option>)}
            </select>
          </div>
          <div className="field"><label>Début</label><input type="time" value={f.startTime} onChange={set('startTime')} /></div>
          <div className="field"><label>Fin</label><input type="time" value={f.endTime} onChange={set('endTime')} /></div>
          <div className="field"><label>Matière *</label>
            <select value={f.subjectId} onChange={set('subjectId')}>
              <option value="">—</option>
              {subjects.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </div>
          <div className="field"><label>Enseignant *</label>
            <select value={f.teacherId} onChange={set('teacherId')}>
              <option value="">—</option>
              {teachers.map((t) => <option key={t.id} value={t.id}>{t.lastName} {t.firstName}</option>)}
            </select>
          </div>
          <div className="field"><label>Salle</label>
            <select value={f.roomId || ''} onChange={set('roomId')}>
              <option value="">—</option>
              {rooms.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
            </select>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 10, marginTop: 14 }}>
          <button className="btn" onClick={submit} style={{ width: 'auto', padding: '0 22px' }}>Ajouter</button>
          <button className="btn-ghost" onClick={onClose}>Annuler</button>
        </div>
      </div>
    </div>
  );
}

function ChangeModal({ session, onClose, onSaved }) {
  const [f, setF] = useState({ type: 'REMPLACEMENT', date: new Date().toISOString().slice(0, 10), substituteTeacher: '', newRoom: '', reason: '' });
  const [error, setError] = useState('');
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  async function submit(e) {
    e.preventDefault(); setError('');
    try { await api.post(`/timetable/${session.id}/changes`, f); onSaved(); }
    catch (err) { setError(err.message); }
  }
  return (
    <div className="modal-overlay">
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <h2>Remplacement / changement</h2>
        <p style={{ color: 'var(--muted)', fontSize: '0.86rem', marginBottom: 16 }}>{session.class?.name} · {session.subject?.name} · {session.startTime}-{session.endTime}</p>
        {error && <div className="form-error">{error}</div>}
        <form onSubmit={submit} className="form-grid">
          <div className="two-col">
            <div className="field"><label>Type</label><select value={f.type} onChange={set('type')}>{Object.entries(CHANGE_TYPES).map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select></div>
            <div className="field"><label>Date</label><input type="date" value={f.date} onChange={set('date')} required /></div>
          </div>
          <div className="two-col">
            <div className="field"><label>Enseignant remplaçant</label><input value={f.substituteTeacher} onChange={set('substituteTeacher')} /></div>
            <div className="field"><label>Nouvelle salle</label><input value={f.newRoom} onChange={set('newRoom')} /></div>
          </div>
          <div className="field"><label>Motif</label><textarea rows="2" value={f.reason} onChange={set('reason')} /></div>
          <div style={{ display: 'flex', gap: 10 }}>
            <button className="btn" type="submit" style={{ width: 'auto', padding: '0 20px' }}>Enregistrer</button>
            <button type="button" className="btn-ghost" onClick={onClose}>Annuler</button>
          </div>
        </form>
      </div>
    </div>
  );
}

const EVENT_TYPES = { FERIE: 'Jour férié', CONGE: 'Congé', EVENEMENT: 'Événement', EXAMEN: 'Examen', REUNION: 'Réunion' };
const EVENT_COLOR = { FERIE: 'red', CONGE: 'gold', EVENEMENT: 'navy', EXAMEN: 'navy', REUNION: 'green' };

function AcademicCalendar() {
  const [rows, setRows] = useState([]);
  const [error, setError] = useState('');
  const [modal, setModal] = useState(null);
  function load() { api.get('/timetable/calendar').then(setRows).catch((e) => setError(e.message)); }
  useEffect(load, []);
  async function archive(id) { if (!(await confirmDialog({ title: 'Retirer', message: 'Retirer cet événement ?', confirmLabel: 'Retirer', danger: true }))) return; try { await api.del(`/timetable/calendar/${id}`); load(); } catch (e) { setError(e.message); } }
  return (
    <div className="panel">
      <div className="panel-head"><h2>Calendrier académique</h2><button className="btn btn-sm" onClick={() => setModal({})}>+ Ajouter</button></div>
      <div className="panel-body">
        {error && <div className="form-error">{error}</div>}
        {rows.length === 0 ? <div className="empty">Aucun événement.</div> : (
          <div className="table-wrap"><table>
            <thead><tr><th>Intitulé</th><th>Type</th><th>Début</th><th>Fin</th><th></th></tr></thead>
            <tbody>{rows.map((e) => (
              <tr key={e.id}>
                <td><b>{e.title}</b>{e.description && <><br /><small style={{ color: 'var(--muted)' }}>{e.description}</small></>}</td>
                <td><span className={`badge ${EVENT_COLOR[e.type] || 'navy'}`}>{EVENT_TYPES[e.type] || e.type}</span></td>
                <td className="mono">{new Date(e.startDate).toLocaleDateString('fr-FR')}</td>
                <td className="mono">{e.endDate ? new Date(e.endDate).toLocaleDateString('fr-FR') : '—'}</td>
                <td style={{ textAlign: 'right' }}><button className="btn-ghost btn-sm" onClick={() => setModal(e)}>Modifier</button><button className="link-danger" onClick={() => archive(e.id)}>Retirer</button></td>
              </tr>
            ))}</tbody>
          </table></div>
        )}
      </div>
      {modal && <EventModal initial={modal} onClose={() => setModal(null)} onSaved={() => { setModal(null); load(); }} />}
    </div>
  );
}

function EventModal({ initial, onClose, onSaved }) {
  const isEdit = !!initial.id;
  const [f, setF] = useState({ title: initial.title || '', type: initial.type || 'EVENEMENT', startDate: initial.startDate ? String(initial.startDate).slice(0, 10) : '', endDate: initial.endDate ? String(initial.endDate).slice(0, 10) : '', description: initial.description || '' });
  const [error, setError] = useState('');
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  async function submit(e) {
    e.preventDefault(); setError('');
    try { if (isEdit) await api.patch(`/timetable/calendar/${initial.id}`, f); else await api.post('/timetable/calendar', f); onSaved(); }
    catch (err) { setError(err.message); }
  }
  return (
    <div className="modal-overlay">
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <h2>{isEdit ? "Modifier l'événement" : 'Nouvel événement'}</h2>
        {error && <div className="form-error">{error}</div>}
        <form onSubmit={submit} className="form-grid">
          <div className="field"><label>Intitulé *</label><input value={f.title} onChange={set('title')} required /></div>
          <div className="two-col">
            <div className="field"><label>Type</label><select value={f.type} onChange={set('type')}>{Object.entries(EVENT_TYPES).map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select></div>
            <div className="field"><label>Début *</label><input type="date" value={f.startDate} onChange={set('startDate')} required /></div>
          </div>
          <div className="field"><label>Fin</label><input type="date" value={f.endDate} onChange={set('endDate')} /></div>
          <div className="field"><label>Description</label><textarea rows="2" value={f.description} onChange={set('description')} /></div>
          <div style={{ display: 'flex', gap: 10 }}><button className="btn" type="submit" style={{ width: 'auto', padding: '0 20px' }}>{isEdit ? 'Enregistrer' : 'Ajouter'}</button><button type="button" className="btn-ghost" onClick={onClose}>Annuler</button></div>
        </form>
      </div>
    </div>
  );
}

function Publication() {
  const [rows, setRows] = useState([]);
  const [error, setError] = useState('');
  const [flash, setFlash] = useState('');
  function load() { api.get('/timetable/publications').then(setRows).catch((e) => setError(e.message)); }
  useEffect(load, []);
  async function publish(row) {
    if (!(await confirmDialog({ title: "Publier l'emploi du temps", message: `Publier l'EDT de ${row.className} ?`, confirmLabel: 'Publier' }))) return;
    try { const r = await api.post(`/timetable/publications/${row.classId}`, {}); setFlash(`EDT de ${r.className} publié.`); load(); }
    catch (e) { setError(e.message); }
  }
  return (
    <div className="panel">
      <div className="panel-head"><h2>Publication & communication</h2></div>
      <div className="panel-body">
        {error && <div className="form-error">{error}</div>}
        {flash && <div className="receipt-banner">{flash}</div>}
        {rows.length === 0 ? <div className="empty">Aucune classe.</div> : (
          <div className="table-wrap"><table>
            <thead><tr><th>Classe</th><th>Dernière publication</th><th></th></tr></thead>
            <tbody>{rows.map((r) => (
              <tr key={r.classId}>
                <td><b>{r.className}</b></td>
                <td>{r.publishedAt ? <><span className="badge green">Publié</span> <span className="mono" style={{ color: 'var(--muted)' }}>{new Date(r.publishedAt).toLocaleString('fr-FR')}</span></> : <span className="badge">Non publié</span>}</td>
                <td style={{ textAlign: 'right' }}><button className="btn btn-sm" onClick={() => publish(r)}>{r.publishedAt ? 'Republier' : 'Publier'}</button></td>
              </tr>
            ))}</tbody>
          </table></div>
        )}
      </div>
    </div>
  );
}

function TimetableSettings() {
  const [s, setS] = useState(null);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);
  const DAYS_OPT = [['1', 'Lun'], ['2', 'Mar'], ['3', 'Mer'], ['4', 'Jeu'], ['5', 'Ven'], ['6', 'Sam'], ['7', 'Dim']];
  useEffect(() => { api.get('/timetable/settings').then(setS).catch((e) => setError(e.message)); }, []);
  if (!s) return <div className="panel"><div className="panel-body"><div className="empty">Chargement…</div></div></div>;
  const days = (s.workingDays || '').split(',').filter(Boolean);
  function toggleDay(d) { const set = new Set(days); set.has(d) ? set.delete(d) : set.add(d); setS({ ...s, workingDays: [...set].sort().join(',') }); setSaved(false); }
  const set = (k) => (e) => { setS({ ...s, [k]: e.target.value }); setSaved(false); };
  async function save(e) { e.preventDefault(); setError(''); try { const r = await api.patch('/timetable/settings', s); setS(r); setSaved(true); } catch (err) { setError(err.message); } }
  return (
    <div className="panel">
      <div className="panel-head"><h2>Paramètres</h2>{saved && <span className="badge green">Enregistré ✓</span>}</div>
      <div className="panel-body" style={{ padding: 18 }}>
        {error && <div className="form-error">{error}</div>}
        <form onSubmit={save}>
          <div className="field" style={{ marginBottom: 14 }}>
            <label>Jours ouvrés</label>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {DAYS_OPT.map(([v, l]) => <button type="button" key={v} className={`tab ${days.includes(v) ? 'active' : ''}`} onClick={() => toggleDay(v)}>{l}</button>)}
            </div>
          </div>
          <div className="grid-3">
            <div className="field"><label>Début de journée</label><input type="time" value={s.dayStart} onChange={set('dayStart')} /></div>
            <div className="field"><label>Fin de journée</label><input type="time" value={s.dayEnd} onChange={set('dayEnd')} /></div>
            <div className="field"><label>Durée créneau (min)</label><input type="number" min="15" step="5" value={s.slotMinutes} onChange={set('slotMinutes')} /></div>
            <div className="field"><label>Pause — début</label><input type="time" value={s.breakStart || ''} onChange={set('breakStart')} /></div>
            <div className="field"><label>Pause — fin</label><input type="time" value={s.breakEnd || ''} onChange={set('breakEnd')} /></div>
          </div>
          <div style={{ marginTop: 16 }}><button className="btn" type="submit" style={{ width: 'auto', padding: '0 22px' }}>Enregistrer</button></div>
        </form>
      </div>
    </div>
  );
}

const ACTION_LABEL = { CREATE: 'Création', UPDATE: 'Modification', ARCHIVE: 'Archivage', PUBLISH: 'Publication', REMPLACEMENT: 'Remplacement', ANNULATION: 'Annulation' };
const ENTITY_LABEL = { TimetableSession: 'Créneau', TimetableChange: 'Changement', TimetablePublication: 'Publication', TimetableSetting: 'Paramètres', AcademicEvent: 'Calendrier' };

function TimetableHistory() {
  const [rows, setRows] = useState([]);
  const [error, setError] = useState('');
  useEffect(() => { api.get('/timetable/history').then(setRows).catch((e) => setError(e.message)); }, []);
  return (
    <div className="panel">
      <div className="panel-head"><h2>Historique & audit</h2></div>
      <div className="panel-body">
        {error && <div className="form-error">{error}</div>}
        {rows.length === 0 ? <div className="empty">Aucune action.</div> : (
          <div className="table-wrap"><table>
            <thead><tr><th>Date</th><th>Action</th><th>Objet</th><th>Par</th></tr></thead>
            <tbody>{rows.map((r) => (
              <tr key={r.id}>
                <td className="mono">{new Date(r.createdAt).toLocaleString('fr-FR')}</td>
                <td>{ACTION_LABEL[r.action] || r.action}</td>
                <td>{ENTITY_LABEL[r.entityType] || r.entityType}</td>
                <td>{r.actor ? `${r.actor.lastName} ${r.actor.firstName}` : '—'}</td>
              </tr>
            ))}</tbody>
          </table></div>
        )}
      </div>
    </div>
  );
}

export default function Timetable() {
  const [tab, setTab] = useState('grille');
  const TABS = [['grille', 'Grille'], ['calendrier', 'Calendrier académique'], ['publication', 'Publication'], ['parametres', 'Paramètres'], ['historique', 'Historique']];
  return (
    <>
      <div className="tabs" style={{ marginBottom: 4 }}>
        {TABS.map(([v, l]) => <button key={v} className={`tab ${tab === v ? 'active' : ''}`} onClick={() => setTab(v)}>{l}</button>)}
      </div>
      {tab === 'grille' && <TimetableGrid />}
      {tab === 'calendrier' && <AcademicCalendar />}
      {tab === 'publication' && <Publication />}
      {tab === 'parametres' && <TimetableSettings />}
      {tab === 'historique' && <TimetableHistory />}
    </>
  );
}

export { TimetableGrid, AcademicCalendar, Publication, TimetableSettings, TimetableHistory };
